/**
 * SCRUM-1811: the Custom Pages chips become query parameters of GET /api/pages
 * (docs/Admin_List_Query.md), so each one holds across every page of the site
 * and not only across the rows of the page on screen. Until then this file held
 * five helpers that filtered and sorted the site's whole pages[] in the browser.
 *
 * The server keeps the rules those helpers applied (SCRUM-1844,
 * `pageChipClauses` in server/controllers/page.js): navigation/footer pages
 * (`kind: chrome`) are hidden by default, `sub_pages=false` also matches a page
 * without the field, and `noindex` is the case-insensitive substring test of
 * SCRUM-1574 on `robots`.
 */

/** Chip value → `sort` parameter. "up" and "recently_added" are the same order. */
const SORTS: Record<string, string> = {
  up: "createdAt:desc",
  down: "createdAt:asc",
  recently_added: "createdAt:desc",
  recently_updated: "updatedAt:desc",
};

/** Chip value → `[filter key, value]`. */
const SETUP: Record<string, [string, string]> = {
  published: ["filter[publish_statement]", "true"],
  unpublished: ["filter[publish_statement]", "false"],
  nav_footer: ["filter[sub_pages]", "true"],
  ex_nav_footer: ["filter[sub_pages]", "false"],
  noindex: ["filter[noindex]", "true"],
};

/** The template-type chips; each value is a `Template.type`. */
const TEMPLATE_TYPE_CHIPS = [
  "intro_page",
  "home_page",
  "privacy_page",
  "listing_page",
  "product_details",
  "contact_form",
  "blog_index",
  "blog_page",
  "author_page",
  "info_article",
  "redirect_page",
  "error_page",
  "sitemap_page",
  "score_method_page",
];

/**
 * The GET /api/pages params for one table page.
 *
 * `filters` is the chip list as FilterContainer stores it, newest first. The
 * server takes one value per key, so where two chips of the same group are
 * selected — two template types, or a sort and a recency chip — the newest one
 * wins. The default order, newest page first, is what the table showed before.
 */
export const pageListParams = ({
  filters = [],
  pageIndex = 0,
  pageSize = 10,
  q = "",
}: { filters?: string[]; pageIndex?: number; pageSize?: number; q?: string }): Record<string, string | number> => {
  const params: Record<string, string | number> = { page: pageIndex + 1, limit: pageSize, sort: SORTS.recently_added };
  const term = q.trim();
  if (term) params.q = term;
  // Oldest first, so a newer chip of the same group overwrites an older one.
  [...filters].reverse().forEach((f) => {
    if (SORTS[f]) params.sort = SORTS[f];
    else if (SETUP[f]) params[SETUP[f][0]] = SETUP[f][1];
    else if (TEMPLATE_TYPE_CHIPS.includes(f)) params["filter[template_type]"] = f;
  });
  return params;
};
